import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlaidLink } from 'react-plaid-link';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { 
  Loader, 
  Building2,
  DollarSign, 
  ArrowLeft,
  AlertCircle,
  Info,
  CreditCard,
  Shield
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../utils/axios';
import './PaymentMethod.css';

const PaymentMethod = () => {
  // State management
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [linkToken, setLinkToken] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('ach');
  const [plaidStatus, setPlaidStatus] = useState({ ready: false, error: null });
  const [userStatus, setUserStatus] = useState(null);

  const navigate = useNavigate();
  const { user } = useAuth();

  // Constants
  const MIN_AMOUNT = 100;
  const MAX_AMOUNT = 1000000;

  // User verification check
  useEffect(() => {
    const checkUserVerification = async () => {
      try {
        setLoading(true);
        // Get user profile data
        const userResponse = await axiosInstance.get('/api/users/profile');
        
        // Try to extract isVerified from different possible response structures
        const userData = userResponse.data?.data || userResponse.data || {};
        const isVerified = userData.isVerified === true;

        if (!isVerified) {
          try {
            // Check KYC status if user is not verified
            const kycResponse = await axiosInstance.get('/api/kyc/status');
            const kycData = kycResponse.data?.data || kycResponse.data || {};
            const kycStatus = kycData.status || 'pending';
            
            setUserStatus({
              kycStatus,
              isVerified: false
            });
          } catch (kycErr) {
            // If KYC status check fails, assume KYC is not submitted
            console.warn('KYC status check error:', kycErr);
            setUserStatus({
              kycStatus: 'not_submitted',
              isVerified: false
            });
          }
        } else {
          // User is verified, set status accordingly
          setUserStatus({
            kycStatus: 'approved',
            isVerified: true
          });
        }
      } catch (err) {
        console.error('Verification check error:', err);
        setError('Unable to verify account status. Please refresh the page or contact support.');
        setUserStatus({
          kycStatus: 'unknown',
          isVerified: false
        });
      } finally {
        setLoading(false);
      }
    };

    checkUserVerification();
  }, []);

  // Initialize Plaid
  useEffect(() => {
    if (paymentMethod === 'ach' && userStatus?.isVerified) {
      initializePlaid();
    }
  }, [paymentMethod, userStatus]);

  const initializePlaid = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Initializing Plaid connection...");
      
      const response = await axiosInstance.post('/api/payments/plaid/create-link-token');
      console.log("Plaid response:", response.data);
      
      if (response.data?.link_token) {
        console.log("Link token received successfully");
        setLinkToken(response.data.link_token);
        setPlaidStatus({ ready: true, error: null });
      } else if (response.data?.success === false) {
        // Handle specific error from our backend
        throw new Error(response.data.message || 'Could not create link token');
      } else {
        throw new Error('Invalid link token received');
      }
    } catch (err) {
      console.error("Plaid initialization error:", err);
      
      // Provide more specific error messages based on the error
      const errorMessage = err.response?.data?.message || 
                         err.response?.data?.error_message ||
                         err.message ||
                         'Unable to initialize bank connection. Please try again.';
      
      setPlaidStatus({
        ready: false,
        error: errorMessage
      });
      setError(errorMessage);
      
      // If we're getting authentication errors, suggest checking credentials
      if (err.response?.status === 401 || 
          err.response?.status === 403 || 
          errorMessage.includes('credentials') || 
          errorMessage.includes('authentication')) {
        console.warn("Possible authentication issue with Plaid");
      }
    } finally {
      setLoading(false);
    }
  };

  // Amount validation
  const validateAmount = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < MIN_AMOUNT) {
      setError(`Minimum deposit amount is $${MIN_AMOUNT}`);
      return false;
    }
    if (numValue > MAX_AMOUNT) {
      setError(`Maximum deposit amount is $${MAX_AMOUNT.toLocaleString()}`);
      return false;
    }
    setError(null);
    return true;
  };

  // Plaid configuration
  const { open, ready, error: plaidError } = usePlaidLink({
    token: linkToken,
    // Set environment appropriate for production vs development
    env: process.env.NODE_ENV === 'production' ? 'production' : 'sandbox',
    // Enhanced Plaid options for better mobile experience
    product: ['auth'],
    clientName: 'Swanpoint',
    countryCodes: ['US'],
    language: 'en',
    onSuccess: async (publicToken, metadata) => {
      try {
        setLoading(true);
        setError(null);
        await handlePlaidSuccess(publicToken, metadata);
      } catch (err) {
        console.error('Plaid onSuccess error:', err);
        
        // Check if this is a KYC verification error
        if (err.response?.data?.requiresKYC) {
          setUserStatus({
            kycStatus: err.response.data.kycStatus || 'not_submitted',
            isVerified: false
          });
        } else {
          setError(err.response?.data?.message || err.message || 'Failed to connect bank account');
        }
      } finally {
        setLoading(false);
      }
    },
    onExit: (err, metadata) => {
      setLoading(false);
      if (err) {
        console.warn('Plaid exit with error:', err);
        if (metadata && metadata.status === 'requires_credentials') {
          setError('Bank authentication required. Please try again.');
        } else if (metadata && metadata.status === 'requires_questions') {
          setError('Additional verification required. Please try again.');
        } else {
          setError('Connection process interrupted');
        }
      }
    },
    onEvent: (eventName, metadata) => {
      console.log('Plaid event:', eventName, metadata);
      if (eventName === 'ERROR') {
        setError(metadata.error_message || 'Connection error occurred');
      } else if (eventName === 'OPEN') {
        // User has opened Plaid Link
        setLoading(true);
      } else if (eventName === 'EXIT') {
        // User has exited Plaid Link
        setLoading(false);
      }
    }
  });
  
  // Handle Plaid errors
  useEffect(() => {
    if (plaidError) {
      console.error('Plaid Link Error:', plaidError);
      setError('An error occurred with the bank connection. Please try again.');
    }
  }, [plaidError]);

  // Handle Plaid success
  const handlePlaidSuccess = async (publicToken, metadata) => {
    try {
      if (!validateAmount(amount)) return;

      // Exchange token
      const exchangeResponse = await axiosInstance.post('/api/payments/plaid/exchange-token', {
        public_token: publicToken,
        account_id: metadata.account_id
      });

      if (!exchangeResponse.data.success) {
        throw new Error('Failed to connect bank account');
      }

      // Initiate transfer
      const transferResponse = await axiosInstance.post('/api/payments/plaid/transfer', {
        amount: parseFloat(amount),
        account_id: metadata.account_id
      });

      if (transferResponse.data.success) {
        navigate('/payment/success', {
          state: {
            transactionId: transferResponse.data.transfer.id,
            amount,
            method: 'Bank Transfer (ACH)',
            estimatedDelivery: transferResponse.data.transfer.estimated_delivery_date
          }
        });
      } else {
        throw new Error('Failed to initiate transfer');
      }
    } catch (err) {
      // Check for KYC-related errors
      if (err.response?.data?.requiresKYC) {
        const kycError = new Error('KYC verification required');
        kycError.response = err.response;
        throw kycError;
      }
      throw new Error(err.response?.data?.message || 'Transfer failed');
    }
  };

  // PayPal handlers
  const createPayPalOrder = async () => {
    try {
      if (!validateAmount(amount)) return null;

      const response = await axiosInstance.post('/api/payments/paypal/create-order', { 
        amount: parseFloat(amount)
      });

      if (!response.data?.id) {
        throw new Error('Invalid PayPal order response');
      }

      return response.data.id;
    } catch (err) {
      console.error('PayPal create order error:', err);
      
      // Check if this is a KYC verification error
      if (err.response?.data?.requiresKYC) {
        setUserStatus({
          kycStatus: err.response.data.kycStatus || 'not_submitted',
          isVerified: false
        });
      } else {
        setError(err.response?.data?.message || 'Failed to create PayPal order. Please try again.');
      }
      return null;
    }
  };

  const handlePayPalApprove = async (data) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.post('/api/payments/paypal/capture-payment', {
        orderID: data.orderID
      });

      if (response.data.success) {
        navigate('/payment/success', {
          state: {
            transactionId: response.data.transactionId,
            amount,
            method: 'PayPal',
            immediate: true
          }
        });
      } else {
        throw new Error('Payment capture failed');
      }
    } catch (err) {
      console.error('PayPal capture error:', err);
      
      // Check if this is a KYC verification error
      if (err.response?.data?.requiresKYC) {
        setUserStatus({
          kycStatus: err.response.data.kycStatus || 'not_submitted',
          isVerified: false
        });
      } else {
        setError(err.response?.data?.message || 'Payment failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Verification check
  if (!userStatus?.isVerified) {
    return (
      <div className="verification-warning">
        <Shield size={24} />
        <div>
          <h2>Verification Required</h2>
          <p>
            {userStatus?.kycStatus === 'pending' 
              ? 'Your verification is being processed. Please check back soon.' 
              : userStatus?.kycStatus === 'rejected' 
                ? 'Your verification was rejected. Please submit again with valid information.'
                : 'Please complete verification before making a deposit.'}
          </p>
          <button 
            onClick={() => navigate('/kyc')}
            disabled={userStatus?.kycStatus === 'pending'}
          >
            {userStatus?.kycStatus === 'pending' 
              ? 'Verification In Progress' 
              : userStatus?.kycStatus === 'rejected'
                ? 'Resubmit Verification'
                : 'Complete Verification'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-container">
      {/* Header */}
      <div className="payment-header">
        <button onClick={() => navigate('/investor')} className="back-button">
          <ArrowLeft size={20} />
          <span>Back to Dashboard</span>
        </button>
        <h1>Make a Deposit</h1>
      </div>

      {/* Error display */}
      {error && (
        <div className="error-message">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Amount input */}
      <div className="amount-input">
        <label htmlFor="amount">Amount (USD)</label>
        <div className="input-wrapper">
          <DollarSign size={20} />
          <input
            type="number"
            id="amount"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              validateAmount(e.target.value);
            }}
            min={MIN_AMOUNT}
            step="0.01"
            placeholder={`Enter amount (min. $${MIN_AMOUNT})`}
            required
          />
        </div>
      </div>

      {/* Payment method selection */}
      <div className="payment-methods">
        <button
          className={paymentMethod === 'ach' ? 'active' : ''}
          onClick={() => setPaymentMethod('ach')}
        >
          <Building2 size={20} />
          <span>Bank Transfer (ACH)</span>
        </button>
        <button
          className={paymentMethod === 'paypal' ? 'active' : ''}
          onClick={() => setPaymentMethod('paypal')}
        >
          <CreditCard size={20} />
          <span>PayPal</span>
        </button>
      </div>

      {/* Payment buttons */}
      {amount && parseFloat(amount) >= MIN_AMOUNT && (
        <div className="payment-actions">
          {paymentMethod === 'ach' ? (
            <button
              onClick={() => open()}
              disabled={!ready || loading}
              className="connect-bank-button"
            >
              {loading ? (
                <Loader className="spinner" />
              ) : (
                <>
                  <Building2 size={20} />
                  Connect Bank Account
                </>
              )}
            </button>
          ) : (
            <div className="paypal-container">
              <PayPalButtons
                createOrder={createPayPalOrder}
                onApprove={handlePayPalApprove}
                onError={(err) => {
                  console.error('PayPal render error:', err);
                  setError('PayPal error: ' + (err.message || 'Failed to load PayPal'));
                }}
                onCancel={() => {
                  console.log('PayPal payment cancelled by user');
                  setLoading(false);
                }}
                style={{ 
                  layout: 'vertical',
                  color: 'blue',
                  shape: 'pill',
                  height: 45,
                  tagline: false
                }}
                disabled={loading}
                fundingSource={undefined}
              />
              {/* Add a fallback message if PayPal fails to load */}
              <div className="paypal-fallback">
                If PayPal doesn't load, please try the bank transfer option instead.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info message */}
      <div className="info-message">
        <Info size={20} />
        <p>
          {paymentMethod === 'ach' 
            ? 'ACH transfers typically take 2-3 business days.'
            : 'PayPal transfers are processed immediately.'
          } Minimum deposit is ${MIN_AMOUNT}.
        </p>
      </div>
    </div>
  );
};

export default PaymentMethod;